import { Pill, ShowMore, Typography } from '@recruit-robin/robin-theme/web-components';
import { IconCallback } from '@recruit-robin/robin-theme/web-icons';

import { useDictionary } from '@/shared/lib/i18n';
import { useStyles } from './Section.styles';

type SectionTitleProps = {
  title: string;
  icon: IconCallback;
};

const SectionTitle = ({ title, icon: Icon }: SectionTitleProps) => {
  const classes = useStyles();

  return (
    <div css={classes.sectionTitle}>
      <Icon size={18} />
      <Typography variant={(v) => v.heading[5]}>{title}</Typography>
    </div>
  );
};

type SectionListProps = {
  items: Array<{ label?: string; badge?: string; isVerified?: boolean }>;
  direction?: 'horizontal' | 'vertical';
};

const SectionList = ({ items, direction = 'vertical' }: SectionListProps) => {
  const classes = useStyles();

  return (
    <div css={[classes.sectionTimeline, direction === 'horizontal' && classes.horizontalSectionContent]}>
      {items.map((item, index) => (
        <div key={`${item.label}-${index}`} css={classes.listItem}>
          {item.label && (
            <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
              {item.label}
            </Typography>
          )}
          {item.badge && (
            <Pill
              key={`${item}-${index}`}
              label={item.badge}
              style="filter-tag"
              type="subtle1"
              size="small"
              padding="text"
            />
          )}
        </div>
      ))}
    </div>
  );
};

type TimelineItem = {
  label: string;
  badge: string;
  description?: string | null;
};

type SectionTimelineProps = {
  items: TimelineItem[];
};

const SectionTimeline = ({ items }: SectionTimelineProps) => {
  const classes = useStyles();
  const dictionary = useDictionary();

  return (
    <div css={classes.sectionTimeline}>
      {items.map((item, index) => (
        <div key={`${item.label}-${index}`} css={classes.timelineItem}>
          <div css={classes.timelineItemHeader}>
            <Typography variant={(v) => v.heading[6]} color={(c) => c.neutral.variant[80]}>
              {item.label}
            </Typography>
            <div css={classes.timelineItemMeta}>
              <Typography variant={(v) => v.body.short} color={(c) => c.neutral.variant[70]}>
                {item.badge}
              </Typography>
            </div>
          </div>
          {item.description && (
            <div css={classes.textContent}>
              <ShowMore.Root lines={2}>
                <ShowMore.Content variant={(v) => v.body.long} color={(c) => c.neutral.variant[70]}>
                  {item.description}
                </ShowMore.Content>
                <ShowMore.More>
                  <Typography
                    css={classes.timelineButton}
                    variant={(v) => v.supporting.label}
                    color={(c) => c.primary.fuchsia[50]}>
                    {dictionary.showMore}
                  </Typography>
                </ShowMore.More>
                <ShowMore.Less>
                  <Typography
                    css={classes.timelineButton}
                    variant={(v) => v.supporting.label}
                    color={(c) => c.primary.fuchsia[50]}>
                    {dictionary.showLess}
                  </Typography>
                </ShowMore.Less>
              </ShowMore.Root>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

type SectionTextProps = {
  content: string;
};

const SectionText = ({ content }: SectionTextProps) => {
  const classes = useStyles();
  const dictionary = useDictionary();

  return (
    <div css={classes.textContent}>
      <ShowMore.Root>
        <ShowMore.Content variant={(v) => v.body.long} color={(c) => c.neutral.variant[70]}>
          {content}
        </ShowMore.Content>
        <ShowMore.More>
          <Typography variant={(v) => v.supporting.label} color={(c) => c.primary.fuchsia[50]}>
            {dictionary.showMore}
          </Typography>
        </ShowMore.More>
        <ShowMore.Less>
          <Typography variant={(v) => v.supporting.label} color={(c) => c.primary.fuchsia[50]}>
            {dictionary.showLess}
          </Typography>
        </ShowMore.Less>
      </ShowMore.Root>
    </div>
  );
};

type SectionPillsProps = {
  items: string[];
};

const SectionPills = ({ items }: SectionPillsProps) => {
  const classes = useStyles();

  return (
    <div css={classes.pillsContainer}>
      {items.map((item, index) => (
        <Pill key={index} label={item} style="filter-tag" type="subtle1" size="small" padding="text" />
      ))}
    </div>
  );
};

type SectionProps = {
  title: React.ReactNode;
  content: React.ReactNode;
};

export const Section = ({ title, content }: SectionProps) => {
  const classes = useStyles();

  return (
    <div css={classes.section}>
      {title}
      <div css={classes.sectionContent}>{content}</div>
    </div>
  );
};

Section.Title = SectionTitle;
Section.List = SectionList;
Section.Timeline = SectionTimeline;
Section.Text = SectionText;
Section.Pills = SectionPills;
